import { healthSample, nodeInventory, nodeStatus } from '@occ/contracts';
import type { AgentCapability, HealthSample, NodeInventory, NodeStatus } from '@occ/contracts';

/**
 * Eventos del WebSocket de estado en vivo (panel ← backend).
 *
 * No viven en `@occ/contracts` porque son la proyección **para el panel** de
 * los `TunnelEvent` que el broker manda al backend: el panel no ve tramas del
 * túnel, ve nodos que cambian de estado. El backend traduce unos en otros.
 * Cuando el workstream A implemente `/ws/events`, este archivo es el contrato
 * que tiene que cumplir.
 */

export type PanelEvent =
  | {
      type: 'node.status';
      nodeId: string;
      status: NodeStatus;
      lastSeenAt: Date | null;
      agentVersion?: string | null;
      capabilities?: AgentCapability[];
    }
  | { type: 'node.health'; nodeId: string; health: HealthSample }
  | { type: 'node.inventory'; nodeId: string; inventory: NodeInventory };

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;

/**
 * Se descartan en silencio los mensajes que no se entienden (`pong`, tipos de
 * una versión más nueva del backend): un panel que se cae porque el servidor
 * añadió un evento es peor que uno que lo ignora.
 */
export const parsePanelEvent = (raw: unknown): PanelEvent | null => {
  const message = asRecord(raw);
  if (!message) return null;
  const nodeId = message.nodeId;
  if (typeof nodeId !== 'string') return null;

  try {
    switch (message.type) {
      case 'node.status': {
        const lastSeen = message.lastSeenAt;
        return {
          type: 'node.status',
          nodeId,
          status: nodeStatus.parse(message.status),
          lastSeenAt: typeof lastSeen === 'string' ? new Date(lastSeen) : null,
          agentVersion: typeof message.agentVersion === 'string' ? message.agentVersion : null,
          capabilities: Array.isArray(message.capabilities)
            ? (message.capabilities as AgentCapability[])
            : undefined,
        };
      }
      case 'node.health':
        return { type: 'node.health', nodeId, health: healthSample.parse(message.health) };
      case 'node.inventory':
        return {
          type: 'node.inventory',
          nodeId,
          inventory: nodeInventory.parse(message.inventory),
        };
      default:
        return null;
    }
  } catch {
    return null;
  }
};
